import Phaser from 'phaser';
import { GameConfig } from '../config/GameConfig';

export class Player extends Phaser.Physics.Arcade.Sprite {
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private keys?: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key };
  public health: number;
  public maxHealth: number;
  public level: number;
  public experience: number;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, '');

    this.health = GameConfig.PLAYER.MAX_HEALTH;
    this.maxHealth = GameConfig.PLAYER.MAX_HEALTH;
    this.level = 1;
    this.experience = 0;

    // Add to scene
    scene.add.existing(this);
    scene.physics.add.existing(this);

    // Create a cat sprite
    this.createCatTexture(scene);
    this.setTexture('player-cat');

    // Setup physics
    this.setCollideWorldBounds(true);
    this.setDrag(500);

    // Setup input
    if (scene.input.keyboard) {
      this.cursors = scene.input.keyboard.createCursorKeys();
      this.keys = {
        W: scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
        A: scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
        S: scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
        D: scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D)
      };
    }
  }

  private createCatTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = GameConfig.PLAYER.SIZE;
    const halfSize = size / 2;

    // Shadow
    graphics.fillStyle(0x000000, 0.2);
    graphics.fillEllipse(halfSize, halfSize + 14, 14, 4);

    // Tail (thicker, curved)
    graphics.fillStyle(0xFF8C00, 1);
    graphics.fillEllipse(halfSize - 8, halfSize + 8, 3, 8);
    graphics.fillCircle(halfSize - 9, halfSize + 12, 2);

    // Body (larger, with shading)
    graphics.fillStyle(0xFFA500, 1);
    graphics.fillEllipse(halfSize, halfSize + 3, 14, 18);

    // Body highlight
    graphics.fillStyle(0xFFB84D, 1);
    graphics.fillEllipse(halfSize - 2, halfSize + 1, 8, 12);

    // Stripes on body
    graphics.lineStyle(1, 0xFF8C00, 0.6);
    graphics.beginPath();
    graphics.moveTo(halfSize - 4, halfSize + 6);
    graphics.lineTo(halfSize + 4, halfSize + 6);
    graphics.strokePath();
    graphics.beginPath();
    graphics.moveTo(halfSize - 3, halfSize + 10);
    graphics.lineTo(halfSize + 3, halfSize + 10);
    graphics.strokePath();

    // Head (larger)
    graphics.fillStyle(0xFFA500, 1);
    graphics.fillCircle(halfSize, halfSize - 4, 11);

    // Head highlight
    graphics.fillStyle(0xFFB84D, 1);
    graphics.fillCircle(halfSize - 2, halfSize - 5, 6);

    // Ears (triangles with inner ear)
    graphics.fillStyle(0xFF8C00, 1);
    graphics.fillTriangle(
      halfSize - 7, halfSize - 10,
      halfSize - 11, halfSize - 17,
      halfSize - 4, halfSize - 14
    );
    graphics.fillTriangle(
      halfSize + 7, halfSize - 10,
      halfSize + 4, halfSize - 14,
      halfSize + 11, halfSize - 17
    );

    // Inner ear
    graphics.fillStyle(0xFFB84D, 1);
    graphics.fillTriangle(
      halfSize - 7, halfSize - 11,
      halfSize - 9, halfSize - 15,
      halfSize - 5, halfSize - 13
    );
    graphics.fillTriangle(
      halfSize + 7, halfSize - 11,
      halfSize + 5, halfSize - 13,
      halfSize + 9, halfSize - 15
    );

    // Eyes (larger, with shine)
    graphics.fillStyle(0x228B22, 1); // Green eyes
    graphics.fillCircle(halfSize - 4, halfSize - 5, 2.5);
    graphics.fillCircle(halfSize + 4, halfSize - 5, 2.5);

    // Eye pupils
    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(halfSize - 4, halfSize - 5, 1.5);
    graphics.fillCircle(halfSize + 4, halfSize - 5, 1.5);

    // Eye shine
    graphics.fillStyle(0xFFFFFF, 1);
    graphics.fillCircle(halfSize - 3.5, halfSize - 5.5, 0.8);
    graphics.fillCircle(halfSize + 4.5, halfSize - 5.5, 0.8);

    // Nose (more detailed)
    graphics.fillStyle(0xFF69B4, 1);
    graphics.fillTriangle(
      halfSize, halfSize,
      halfSize - 2, halfSize - 2.5,
      halfSize + 2, halfSize - 2.5
    );

    // Mouth
    graphics.lineStyle(1, 0x000000, 0.6);
    graphics.beginPath();
    graphics.arc(halfSize - 2, halfSize + 1, 2, 0, Math.PI, false);
    graphics.strokePath();
    graphics.beginPath();
    graphics.arc(halfSize + 2, halfSize + 1, 2, 0, Math.PI, false);
    graphics.strokePath();

    // Whiskers
    graphics.lineStyle(1, 0xFFFFFF, 0.8);
    // Left whiskers
    graphics.beginPath();
    graphics.moveTo(halfSize - 8, halfSize - 2);
    graphics.lineTo(halfSize - 14, halfSize - 4);
    graphics.strokePath();
    graphics.beginPath();
    graphics.moveTo(halfSize - 8, halfSize);
    graphics.lineTo(halfSize - 14, halfSize);
    graphics.strokePath();
    // Right whiskers
    graphics.beginPath();
    graphics.moveTo(halfSize + 8, halfSize - 2);
    graphics.lineTo(halfSize + 14, halfSize - 4);
    graphics.strokePath();
    graphics.beginPath();
    graphics.moveTo(halfSize + 8, halfSize);
    graphics.lineTo(halfSize + 14, halfSize);
    graphics.strokePath();

    graphics.generateTexture('player-cat', size, size);
    graphics.destroy();
  }

  update(): void {
    this.handleMovement();
  }

  private handleMovement(): void {
    if (!this.cursors || !this.keys) return;

    const velocity = new Phaser.Math.Vector2(0, 0);

    // WASD or Arrow keys
    if (this.cursors.left.isDown || this.keys.A.isDown) {
      velocity.x = -1;
    } else if (this.cursors.right.isDown || this.keys.D.isDown) {
      velocity.x = 1;
    }

    if (this.cursors.up.isDown || this.keys.W.isDown) {
      velocity.y = -1;
    } else if (this.cursors.down.isDown || this.keys.S.isDown) {
      velocity.y = 1;
    }

    // Normalize diagonal movement
    velocity.normalize();

    // Use custom move speed if set, otherwise use default
    const moveSpeed = this.getData('moveSpeed') || GameConfig.PLAYER.SPEED;
    velocity.scale(moveSpeed);

    this.setVelocity(velocity.x, velocity.y);
  }

  takeDamage(amount: number): void {
    this.health = Math.max(0, this.health - amount);

    // Visual feedback
    this.setTint(0xff0000);
    this.scene.time.delayedCall(100, () => {
      this.clearTint();
    });

    if (this.health <= 0) {
      this.die();
    }
  }

  addExperience(amount: number): void {
    this.experience += amount;
    this.checkLevelUp();
  }

  private checkLevelUp(): void {
    const xpRequired = this.getXPForNextLevel();

    if (this.experience >= xpRequired) {
      this.levelUp();
    }
  }

  public getXPForNextLevel(): number {
    // Very easy progression: 50 for all early levels
    if (this.level <= 3) return 50;
    return 50 + ((this.level - 3) * 30);
  }

  private levelUp(): void {
    this.level++;
    this.experience = 0; // Reset XP for next level

    // Emit level up event for the scene to handle
    this.scene.events.emit('player-levelup');

    // Visual feedback
    this.setTint(0x00FF00);
    this.scene.time.delayedCall(200, () => {
      this.clearTint();
    });
  }

  private die(): void {
    // Trigger game over screen
    (this.scene as any).showGameOver();
  }
}
